import React, { useState, useEffect } from 'react';
import {
    History as HistoryIcon,
    Schedule as ScheduleIcon,
    Computer as ComputerIcon,
    Security as SecurityIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import axios from 'axios';
import './AccountActivity.css';

const AccountActivity = () => {
    const [auditLogs, setAuditLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [userId, setUserId] = useState('');

    useEffect(() => {
        const currentUserId = sessionStorage.getItem('userId');        
        if (currentUserId) {
            setUserId(currentUserId);
            fetchAuditTrail(currentUserId);
        }
    }, []);

    const sortAuditLogsByDate = (logs) => {
        return logs.sort((a, b) => new Date(b.timeCreated) - new Date(a.timeCreated));
    };

    const fetchAuditTrail = async (userId) => {
        if (!userId) return;
        setLoading(true);
        
        try {
            const baseUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.get(`${baseUrl}/Auth/audit/${userId}`);
            const data = response.data;

            // Handle direct array response
            if (Array.isArray(data)) {
                setAuditLogs(sortAuditLogsByDate([...data]));
            } 
            // Handle wrapped response with isSuccess
            else if (data?.isSuccess && data?.data) {
                const logs = Array.isArray(data.data) ? data.data : (data.data?.recordList || []);
                setAuditLogs(sortAuditLogsByDate([...logs]));
            } 
            // Handle any other structure
            else {
                setAuditLogs([]);
            }
        } catch (error) {
            console.error('Error fetching audit trail:', error);
            setAuditLogs([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getModuleIcon = (module) => {
        if (module.toLowerCase().includes('authentication')) {
            return <SecurityIcon className="icon-auth" />;
        } else if (module.toLowerCase().includes('api')) {
            return <ComputerIcon className="icon-api" />;
        } else {
            return <HistoryIcon className="icon-activity" />;
        }
    };

    return (
        <div className="account-activity-container">
            {/* Header */}
            <div className="activity-header">
                <div className="header-content">
                    <div className="header-left">
                        <div className="header-icon-wrapper">
                            <HistoryIcon className="header-icon" />
                        </div>
                        <div className="header-text">
                            <h1 className="header-title">Account Activity</h1>
                            <p className="header-subtitle">View your account activity history</p>
                        </div>
                    </div>
                    <button
                        onClick={() => fetchAuditTrail(userId)}
                        className="refresh-button"
                    >
                        <RefreshIcon />
                        <span>Refresh</span>
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            {auditLogs.length > 0 && (
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-content">
                            <div className="stat-icon-wrapper stat-icon-green">
                                <HistoryIcon className="stat-icon" />
                            </div>
                            <div className="stat-info">
                                <p className="stat-label">Total Activities</p>
                                <p className="stat-value">{auditLogs.length}</p>
                            </div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-content">
                            <div className="stat-icon-wrapper stat-icon-blue">
                                <SecurityIcon className="stat-icon" />
                            </div>
                            <div className="stat-info">
                                <p className="stat-label">Auth Events</p>
                                <p className="stat-value">
                                    {auditLogs.filter(log => log.module.toLowerCase().includes('authentication')).length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-content">
                            <div className="stat-icon-wrapper stat-icon-purple">
                                <ComputerIcon className="stat-icon" />
                            </div>
                            <div className="stat-info">
                                <p className="stat-label">API Calls</p>
                                <p className="stat-value">
                                    {auditLogs.filter(log => log.module.toLowerCase().includes('api')).length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-content">
                            <div className="stat-icon-wrapper stat-icon-orange">
                                <ScheduleIcon className="stat-icon" />
                            </div>
                            <div className="stat-info">
                                <p className="stat-label">Last Activity</p>
                                <p className="stat-value-small">
                                    {auditLogs.length > 0 ? formatDateTime(auditLogs[0]?.timeCreated) : 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Activity Log Table */}
            <div className="activity-log-container">
                <div className="activity-log-header">
                    <h2 className="activity-log-title">
                        <HistoryIcon />
                        <span>Activity Log</span>
                    </h2>
                </div>

                {loading ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p className="loading-text">Loading activity log...</p>
                    </div>
                ) : auditLogs.length > 0 ? (
                    <div className="table-wrapper">
                        <table className="activity-table">
                            <thead>
                                <tr>
                                    <th>Module</th>
                                    <th>Activity</th>
                                    <th>Date & Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {auditLogs.map((log) => (
                                    <tr key={log.id}>
                                        <td>
                                            <div className="module-cell">
                                                {getModuleIcon(log.module)}
                                                <span className="module-name">{log.module}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="activity-message">
                                                {log.message}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="datetime-cell">
                                                <ScheduleIcon className="datetime-icon" fontSize="small" />
                                                <span>{formatDateTime(log.timeCreated)}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="empty-state">
                        <div className="empty-icon-wrapper">
                            <HistoryIcon className="empty-icon" />
                        </div>
                        <p className="empty-title">No Activity Found</p>
                        <p className="empty-subtitle">
                            Your account activity will appear here
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AccountActivity;